import * as React from "react";
import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import settings from "../appSettings";
import Toolbar from "@mui/material/Toolbar";
import Divider from "@mui/material/Divider";
import Container from "@mui/material/Container";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import Landing from "../pages/landing/landingPage";
import MeetingLibraryPage from "../pages/library/meetingLibraryPage";
import ItemsPage from "../pages/notes/itemsPage";
import ActorDashboard from "../pages/dashboards/actorDashboardPage";
import CreatorDashboard from "../pages/dashboards/creatorDashboardPage";
import UsersPage from "../pages/management/usersPage";
import { ReviewPage } from "../pages/notes/reviewPage";
import MainHeader from "./mainHeader";
import MainNav from "./mainNav";
import { useEffect, useMemo } from "react";
import { bindActionCreators } from "../actions/actionCreators";
import { ItemActions } from "../actions/itemActions";
import { ListActions } from "../actions/listActions";
import { Context } from "../components/context";
import { AppContext } from "../models/applicationState";
import * as itemActions from "../actions/itemActions";
import * as listActions from "../actions/listActions";
import MeetingLibraryItemsPage from "../pages/library/meetingLibraryItemsPage";
import ProjectPanel from "../components/projectPanel";
import ProjectsPage from "../pages/management/projectsPage";
import * as userActions from "../actions/userActions";
import { useMsal } from "@azure/msal-react";
import {
  AuthenticationResult,
  EventMessage,
  EventType,
} from "@azure/msal-browser";
import { UserActions } from "../actions/userActions";
import {
  doSelectedProjectIdsExist,
  doSelectedTagIdsExist,
  storeSelectedProjectIds,
} from "../util/filterHandler";
import {
  createTheme,
  ThemeProvider,
  darken,
  lighten,
} from "@mui/material/styles";
import { deDE as deDECore, enUS as enUSCore } from "@mui/material/locale";
import { deDE as deDEGrid, enUS as enUSGrid } from "@mui/x-data-grid/locales";
import { isEfesoDomain, isRtDomain, isGcDomain } from "../config";
import { Button, Typography, Fab } from "@mui/material";
import { useTranslation } from "react-i18next";
import { acceptTerms } from "../services/userService";
import { grey } from "@mui/material/colors";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import "dayjs/locale/de";
import Events from "../pages/calendar/eventsPage";
import TagLibraryPage from "../pages/library/tagLibraryPage";
import TagItemsPage from "../pages/library/tagItemsPage";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import QuickTasksPanel from "../components/quickTasksPanel";

import CoachingHub from "../pages/coachingNavigator/coachingHub";
import CoachingSessions from "../pages/coachingNavigator/coachingSessions";
import CoachingManagement from "../pages/coachingNavigator/management/coachingManagement";
import MethodPage from "../pages/coachingNavigator/management/methodPage";
import ClientPage from "../pages/coachingNavigator/management/clientPage";
import EmailTemplatesPage from "../pages/coachingNavigator/management/emailTempPage";
import SessionDetails from "../pages/coachingNavigator/sessiondetails/sessionDetails";
import ClientOverview from "../pages/coachingNavigator/clientOverview";
import DistributionScreen from "../pages/coachingNavigator/distributionScreen";
import SessionOverview from "../pages/coachingNavigator/sessionOverview";
import CoachingCalendar from "../pages/coachingNavigator/coachingCalendar";
import { UserRole } from "../models";
import MeetingNotes from "../pages/notes/meetingNotes";

const drawerWidth = 260;

// MUI X v9 DataGrid uses `palette.DataGrid?.bg ?? palette.background.paper`
// for its root background. Our brand themes use `background.paper` for the
// page surface (baby blue / turquoise), so we override DataGrid colors back
// to white to match the v6 visual.
const dataGridPalette = {
  bg: "#FFFFFF",
  headerBg: "#FFFFFF",
  pinnedBg: "#FFFFFF",
};

const hiddenPickerScrollbar = {
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
  "&::-webkit-scrollbar": {
    width: 0,
    height: 0,
  },
};

const getSharedComponents = (noRowsLabel: string) => ({
  MuiDataGrid: {
    defaultProps: {
      localeText: {
        noRowsLabel,
      },
    },
  },
  MuiDataGridPro: {
    defaultProps: {
      localeText: {
        noRowsLabel,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#d2d2d2",
          transition: "border-color 0.15s ease, border-width 0.15s ease",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#242C63",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#242C63",
          borderWidth: "2px",
        },
        "&.Mui-disabled": {
          backgroundColor: "#F8FAFC",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#d2d2d2",
          },
        },
      },
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        "& .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: "#d2d2d2",
          transition: "border-color 0.15s ease, border-width 0.15s ease",
        },
        "&:hover .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: "#242C63",
        },
        "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: "#242C63",
          borderWidth: "2px",
        },
        "&.Mui-disabled, &.Mui-disabled:hover": {
          backgroundColor: "#F8FAFC",
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "#d2d2d2",
          },
        },
      },
    },
  },
  MuiPickersPopper: {
    styleOverrides: {
      paper: {
        backgroundColor: "#FFFFFF",
      },
    },
  },
  MuiPickersLayout: {
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
        "& .MuiPickersLayout-contentWrapper": {
          backgroundColor: "#FFFFFF",
        },
      },
    },
  },
  MuiPickersActionBar: {
    styleOverrides: {
      root: {
        justifyContent: "center",
        paddingLeft: 16,
        paddingRight: 16,
      },
    },
  },
  MuiDateCalendar: {
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
      },
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
      },
    },
  },
  MuiDigitalClock: {
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
        ...hiddenPickerScrollbar,
        "& .MuiMenuItem-root": {
          backgroundColor: "#FFFFFF",
          color: "#242C63",
        },
        "& .MuiMenuItem-root.Mui-selected": {
          backgroundColor: "#242C63",
          color: "#FFFFFF",
        },
        "& .MuiMenuItem-root.Mui-selected:hover": {
          backgroundColor: "#353D7A",
          color: "#FFFFFF",
        },
        "& .MuiMenuItem-root:hover": {
          backgroundColor: "#F8FAFC",
        },
      },
    },
  },
  MuiMultiSectionDigitalClock: {
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
      },
    },
  },
  MuiMultiSectionDigitalClockSection: {
    styleOverrides: {
      root: {
        backgroundColor: "#FFFFFF",
        ...hiddenPickerScrollbar,
        "& .MuiMenuItem-root": {
          backgroundColor: "#FFFFFF",
          color: "#242C63",
        },
        "& .MuiMenuItem-root.Mui-selected": {
          backgroundColor: "#242C63",
          color: "#FFFFFF",
        },
        "& .MuiMenuItem-root.Mui-selected:hover": {
          backgroundColor: "#353D7A",
          color: "#FFFFFF",
        },
        "& .MuiMenuItem-root:hover": {
          backgroundColor: "#F8FAFC",
        },
      },
    },
  },
});

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    // marginLeft: drawerWidth,
    // width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    backgroundColor: theme.palette.background.default,
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

export default function Layout() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { instance } = useMsal();

  const [loggedInUserEmail, setLoggedInUserEmail] =
    React.useState<string>(undefined);
  const [justAcceptedTermsAndConditions, setJustAcceptedTermsAndConditions] =
    React.useState<boolean>(false);
  const [quickTasksOpen, setQuickTasksOpen] = React.useState(false);
  const appContext = React.useContext<AppContext>(Context);

  const handleOpenQuickTasks = React.useCallback(() => {
    setQuickTasksOpen(true);
  }, []);

  const handleCloseQuickTasks = React.useCallback(() => {
    setQuickTasksOpen(false);
  }, []);

  const actions = React.useMemo(
    () => ({
      lists: bindActionCreators(
        listActions,
        appContext.dispatch,
      ) as unknown as ListActions,
      items: bindActionCreators(
        itemActions,
        appContext.dispatch,
      ) as unknown as ItemActions,
      users: bindActionCreators(
        userActions,
        appContext.dispatch,
      ) as unknown as UserActions,
    }),
    [appContext.dispatch],
  );

  const getInvokerUser = React.useCallback(async () => {
    actions.users.getInvoker();
  }, [actions.users]);

  useEffect(() => {
    if (
      !loggedInUserEmail ||
      loggedInUserEmail != appContext.state.invoker?.email
    ) {
      getInvokerUser();
    }
  }, [loggedInUserEmail, appContext.state.invoker, getInvokerUser]);

  useEffect(() => {
    if (appContext.state.invoker?.projects?.length) {
      if (!doSelectedProjectIdsExist() && !doSelectedTagIdsExist()) {
        storeSelectedProjectIds([]);
        setOpenProjectPanel(true);
      }
    }
  }, [appContext.state.invoker?.projects]);

  useEffect(() => {
    if (!instance) {
      return;
    }
    console.info(`Adding listener to ${instance}`);
    const callbackId = instance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        setLoggedInUserEmail(payload?.account?.username?.toLowerCase());
      } else if (
        event.eventType === EventType.LOGOUT_SUCCESS &&
        event.payload
      ) {
        setLoggedInUserEmail(null);
      } else if (
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS &&
        event.payload
      ) {
        const payload = event.payload as AuthenticationResult;
        // Only update logged-in user for tokens from the app's own tenant,
        // not for calendar-connected personal accounts.
        if (payload?.account?.tenantId === settings.tenantId) {
          setLoggedInUserEmail(payload?.account?.username?.toLowerCase());
        }
      }
    });
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  const [open, setOpen] = React.useState(true);
  const [openProjectPanel, setOpenProjectPanel] = React.useState(false);

  const renderCoachOnly = (element: React.ReactElement) => {
    if (!appContext.state.invoker?.role) {
      return null;
    }

    if (appContext.state.invoker.role === UserRole.Coach) {
      return element;
    }

    return <Navigate to="/leadership" replace />;
  };

  // Load meetings
  useEffect(() => {
    if (!appContext.state.lists) {
      actions.lists.list();
    }
  }, [actions.lists, appContext.state.lists]);

  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const toggleProjectPanel = () => {
    setOpenProjectPanel((prev) => !prev);
  };

  const canShowQuickTaskFab = React.useMemo(
    () =>
      Boolean(
        appContext.state.invoker?.termsAcceptanceDate ||
        justAcceptedTermsAndConditions,
      ),
    [
      appContext.state.invoker?.termsAcceptanceDate,
      justAcceptedTermsAndConditions,
    ],
  );
  const showFabLabel = canShowQuickTaskFab && open;

  const isGerman = i18n.language?.toLowerCase().startsWith("de");

  useEffect(() => {
    dayjs.locale(isGerman ? "de" : "en-gb");
  }, [isGerman]);

  const noRowsLabel = t("toolbar.noRowsLabel");

  const theme = useMemo(
    () =>
      createTheme(
        {
          cssVariables: true,
          palette: {
            primary: {
              main: "#242C63",
            },
            secondary: {
              main: "#777586",
            },
            action: {
              hover: "#F1EAE3",
              selected: darken("#F1EAE3", 0.1),
              focus: darken("#F1EAE3", 0.15),
            },
            background: {
              default: "#FFF",
              paper: "#E9ECF5",
            },
            DataGrid: dataGridPalette,
          },
        },
        isGerman ? deDECore : enUSCore,
        isGerman ? deDEGrid : enUSGrid,
        {
          components: getSharedComponents(noRowsLabel),
        },
      ),
    [isGerman, noRowsLabel],
  );

  // White label adaption for customer EFESO
  const efesoTheme = useMemo(
    () =>
      createTheme(
        {
          cssVariables: true,
          palette: {
            primary: {
              main: "#3B3D43",
            },
            secondary: {
              main: "#385D7F",
            },
            action: {
              hover: grey[200],
              selected: lighten("#EF7A13", 0.75),
              focus: lighten("#EF7A13", 0.5),
            },
            background: {
              default: "#FFF",
              paper: "#E9ECF5",
            },
            text: {
              primary: "#385D7F",
            },
            DataGrid: dataGridPalette,
          },
        },
        isGerman ? deDECore : enUSCore,
        isGerman ? deDEGrid : enUSGrid,
        {
          components: getSharedComponents(noRowsLabel),
        },
      ),
    [isGerman, noRowsLabel],
  );

  // White label adaption for customer RTConsulting
  const rtTheme = useMemo(
    () =>
      createTheme(
        {
          cssVariables: true,
          palette: {
            primary: {
              main: "#16116D",
            },
            secondary: {
              main: "#777586",
            },
            action: {
              hover: "#715979",
              selected: darken("#715979", 0.1),
              focus: darken("#715979", 0.15),
            },
            background: {
              default: "#FFF",
              paper: "#6DD7CD",
            },
            DataGrid: dataGridPalette,
          },
        },
        isGerman ? deDECore : enUSCore,
        isGerman ? deDEGrid : enUSGrid,
        {
          components: getSharedComponents(noRowsLabel),
        },
      ),
    [isGerman, noRowsLabel],
  );

  // White Label adoption for Customer GC
  const gcTheme = useMemo(
    () =>
      createTheme(
        {
          cssVariables: true,
          palette: {
            primary: {
              main: "#242C63",
            },
            secondary: {
              main: "#777586",
            },
            action: {
              hover: "#F1EAE3",
              selected: darken("#F1EAE3", 0.1),
              focus: darken("#F1EAE3", 0.15),
            },
            background: {
              default: "#FFF",
              paper: "#E9ECF5",
            },
            DataGrid: dataGridPalette,
          },
        },
        isGerman ? deDECore : enUSCore,
        isGerman ? deDEGrid : enUSGrid,
        {
          components: getSharedComponents(noRowsLabel),
        },
      ),
    [isGerman, noRowsLabel],
  );

  let selectedTheme = theme;
  if (isEfesoDomain()) {
    selectedTheme = efesoTheme;
  }
  if (isRtDomain()) {
    selectedTheme = rtTheme;
  }
  if (isGcDomain()) {
    selectedTheme = gcTheme;
  }

  function accept(_event): void {
    acceptTerms();
    setJustAcceptedTermsAndConditions(true);
  }

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={isGerman ? "de" : "en-gb"}
    >
      <ThemeProvider theme={selectedTheme}>
        {appContext.state.invoker &&
          !appContext.state.invoker?.termsAcceptanceDate &&
          !justAcceptedTermsAndConditions && (
            <Box>
              <CssBaseline />
              <Box
                component="main"
                sx={(theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100vh",
                  gap: 4,
                  backgroundColor: theme.palette.grey[900],
                  overflow: "auto",

                  ...theme.applyStyles("light", {
                    backgroundColor: theme.palette.background.paper,
                  }),
                })}
              >
                {/* <Container maxWidth={false} sx={{ mt: 0, mb: 0 }}> */}
                <Box
                  component="img"
                  sx={{
                    width: "30vw",
                    marginBottom: 2,
                  }}
                  alt="Pinnacle logo"
                  src="/logo.svg"
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                    <a
                      target="_blank"
                      href="https://www.leadwithpinnacle.com/termsofuse"
                    >
                      {t("about.terms_conditions")}
                    </a>
                    <br />
                    {t("about.copyright")}
                  </Typography>

                  <br />
                  <br />

                  <Button
                    variant="contained"
                    onClick={accept}
                    sx={{ width: 460 }}
                  >
                    <Typography
                      variant="button"
                      sx={{
                        display: "block",
                      }}
                    >
                      {t("about.accept_terms")}
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        {(appContext.state.invoker?.termsAcceptanceDate ||
          justAcceptedTermsAndConditions) && (
          <Box sx={{ display: "flex" }}>
            <CssBaseline />
            <AppBar position="absolute" open={open}>
              <MainHeader
                toggleDrawer={toggleDrawer}
                open={open}
                openProjectPanel={openProjectPanel}
                showProjectPanel={!!appContext.state.invoker?.projects?.length}
                toggleProjectPanel={toggleProjectPanel}
              ></MainHeader>
            </AppBar>

            <Drawer variant="permanent" open={open}>
              {/* {console.log("DRAWER MENU " + open)} */}
              <Toolbar
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  px: [1],
                }}
              >
                {/* <IconButton onClick={toggleDrawer}>
                            <ChevronLeftIcon />
                        </IconButton> */}
              </Toolbar>
              <Divider />
              <Box
                sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pb: 8 }}
              >
                <MainNav open={open} />
              </Box>
            </Drawer>

            <Box
              component="main"
              sx={(theme) => ({
                width: `calc(100vw - ${drawerWidth}px)`,
                backgroundColor: theme.palette.grey[900],

                // height: '100vh',
                // overflow: 'auto',
                flexGrow: 1,

                ...theme.applyStyles("light", {
                  backgroundColor: theme.palette.background.paper,
                }),
              })}
            >
              <Toolbar />
              <Container
                maxWidth={false}
                sx={{
                  mt: 0,
                  mb: 0,
                  height: `calc(100vh - 64px)`,
                  overflow: "auto",
                }}
              >
                <Routes>
                  <Route path="/leadership" element={<Landing />} />
                  <Route path="/library" element={<MeetingLibraryPage />} />
                  <Route
                    path="/library/:meetingId"
                    element={<MeetingLibraryItemsPage />}
                  />
                  <Route
                    path="/library/:meetingId/current"
                    element={<MeetingLibraryItemsPage />}
                  />
                  <Route
                    path="/library/:meetingId/published"
                    element={<MeetingLibraryItemsPage />}
                  />
                  {/* Meeting Notes workflow starts at /meetings and uses a dedicated sub-route for selected meetings */}
                  <Route path="/meetings" element={<MeetingNotes />} />
                  <Route
                    path="/meetings/workflow/:meetingId"
                    element={<MeetingNotes />}
                  />
                  <Route path="/meetings/:meetingId" element={<ItemsPage />} />
                  <Route
                    path="/meetings/:meetingId/review"
                    element={<ReviewPage />}
                  />
                  <Route path="/events" element={<Events />} />
                  <Route path="/dashboard/actor" element={<ActorDashboard />} />
                  <Route
                    path="/dashboard/creator"
                    element={<CreatorDashboard />}
                  />
                  <Route path="/tags/all" element={<TagLibraryPage />} />
                  <Route
                    path="/tags/:projectId/:tagId"
                    element={<TagItemsPage />}
                  />
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/coaching-hub" element={<CoachingHub />} />
                  <Route
                    path="/coaching-hub/:coacheeId"
                    element={<ClientOverview />}
                  />
                  <Route path="/sessions" element={<CoachingSessions />} />
                  <Route path="/management" element={<CoachingManagement />} />
                  <Route path="/management/methods" element={<MethodPage />} />
                  <Route path="/management/clients" element={<ClientPage />} />
                  <Route
                    path="/management/email-templates"
                    element={<EmailTemplatesPage />}
                  />
                  <Route
                    path="/sessions/:sessionId"
                    element={<SessionDetails />}
                  />
                  <Route
                    path="/sessions/:sessionId/distribute"
                    element={<DistributionScreen />}
                  />
                  <Route
                    path="/sessionOverview/:clientId"
                    element={<SessionOverview />}
                  />
                  <Route
                    path="/coaching-calendar"
                    element={renderCoachOnly(<CoachingCalendar />)}
                  />
                  <Route path="/" element={<Navigate to="/leadership" />} />
                </Routes>
              </Container>
            </Box>

            {appContext.state.invoker?.projects?.length && (
              <MuiDrawer anchor="right" open={openProjectPanel}>
                <ProjectPanel
                  togglePanel={toggleProjectPanel}
                  projects={appContext.state.invoker?.projects}
                />
              </MuiDrawer>
            )}
          </Box>
        )}
        {/* Global FAB for creating a new note */}
        <Box>
          {canShowQuickTaskFab && (
            <Fab
              color="primary"
              variant="extended"
              onClick={handleOpenQuickTasks}
              aria-label={t("quicktask.layout_fab_label")}
              sx={[
                (theme) => ({
                  position: "fixed",
                  bottom: 24,
                  left: 24,
                  zIndex: theme.zIndex.drawer + 1,
                  color: "white",
                  borderRadius: "50px",
                  padding: "12px 16px",
                  fontSize: "16px",
                  textTransform: "none",
                  boxShadow: "0px 4px 12px rgba(43, 62, 116, 0.3)",

                  "&:hover": {
                    backgroundColor: "#1f2d54",
                    boxShadow: "0px 6px 16px rgba(43, 62, 116, 0.5)",
                  },

                  gap: 1,
                }),
                quickTasksOpen
                  ? {
                      opacity: 0.5,
                    }
                  : {
                      opacity: 1,
                    },
              ]}
            >
              <NoteAddOutlinedIcon />
              <Typography variant="body1">
                {t("quicktask.layout_fab_label")}
              </Typography>
            </Fab>
          )}
        </Box>

        {/* Quick Tasks Dialog */}
        <QuickTasksPanel
          open={quickTasksOpen}
          onClose={handleCloseQuickTasks}
        />
      </ThemeProvider>
    </LocalizationProvider>
  );
}
